import firebaseClientDefaults from "./firebaseClientDefaults.json";

const LOOPBACK_HOSTS = new Set(["127.0.0.1", "localhost", "::1"]);

const FIREBASE_CONFIG_FIELDS = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];

function enabled(value) {
  return String(value || "").trim().toLowerCase() === "true";
}

function isProduction(env) {
  return String(env.NODE_ENV || "").trim().toLowerCase() === "production";
}

function isDemoValue(value) {
  const normalised = String(value || "").trim().toLowerCase();
  return (
    normalised === "demo" ||
    normalised === "demo-only" ||
    normalised.startsWith("demo-") ||
    normalised.includes(".demo-") ||
    normalised.includes(":demo") ||
    normalised.includes("localhost") ||
    normalised.includes("127.0.0.1") ||
    normalised.includes("0.0.0.0") ||
    normalised === "::1" ||
    normalised.includes("[::1]") ||
    normalised.includes("${") ||
    normalised.includes("your_project") ||
    normalised.includes("your_api_key") ||
    normalised.includes("xxxxxxxx") ||
    /^0+$/.test(normalised)
  );
}

function emulatorPort(value, fallback, name) {
  const port = value === undefined || value === "" ? fallback : Number(value);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    throw new Error(`${name} must be a valid local emulator port.`);
  }
  return port;
}

export function getLocalFirebaseEmulatorConfig(env = process.env, firebaseConfig = {}) {
  const useEmulators = enabled(env.REACT_APP_USE_FIREBASE_EMULATORS);
  const communityAgentEnabled = enabled(env.REACT_APP_UUN_COMMUNITY_AGENT_ENABLED);
  const projectId = String(firebaseConfig.projectId || "");

  if (isProduction(env) && useEmulators) {
    throw new Error(
      "Production must not connect to Firebase emulators. Keep emulator settings in .env.development.local.",
    );
  }
  if (isProduction(env) && isDemoValue(projectId)) {
    throw new Error(
      "Production must not use a demo Firebase project configuration.",
    );
  }

  if (communityAgentEnabled && !useEmulators) {
    throw new Error(
      "Community Information Agent local mode requires all Firebase emulators.",
    );
  }
  if (communityAgentEnabled && !projectId.startsWith("demo-")) {
    throw new Error(
      "Community Information Agent local mode requires a demo-* Firebase project ID.",
    );
  }
  if (!useEmulators) return null;

  const host = String(env.REACT_APP_FIREBASE_EMULATOR_HOST || "127.0.0.1").trim();
  if (!LOOPBACK_HOSTS.has(host)) {
    throw new Error("Firebase emulator host must be a loopback address.");
  }

  return {
    host,
    authPort: emulatorPort(
      env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT,
      9099,
      "Auth emulator port",
    ),
    firestorePort: emulatorPort(
      env.REACT_APP_FIREBASE_FIRESTORE_EMULATOR_PORT,
      8080,
      "Firestore emulator port",
    ),
    functionsPort: emulatorPort(
      env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT,
      5001,
      "Functions emulator port",
    ),
    storagePort: emulatorPort(
      env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT,
      9199,
      "Storage emulator port",
    ),
  };
}

export function isCommunityAgentLocalMode(env = process.env) {
  return (
    !isProduction(env) &&
    enabled(env.REACT_APP_UUN_COMMUNITY_AGENT_ENABLED) &&
    enabled(env.REACT_APP_USE_FIREBASE_EMULATORS) &&
    String(env.REACT_APP_FIREBASE_PROJECT_ID || "").startsWith("demo-")
  );
}

export function assertProductionFirebaseConfig(
  env = process.env,
  firebaseConfig = {},
) {
  if (!isProduction(env)) return true;

  if (enabled(env.REACT_APP_USE_FIREBASE_EMULATORS)) {
    throw new Error(
      "Production must not connect to Firebase emulators. Keep emulator settings in .env.development.local.",
    );
  }

  const unsafeField = FIREBASE_CONFIG_FIELDS.find((field) =>
    isDemoValue(firebaseConfig[field]),
  );
  if (unsafeField) {
    throw new Error(
      `Production Firebase configuration contains a local/demo ${unsafeField}.`,
    );
  }

  if (!String(firebaseConfig.projectId || "").trim()) {
    throw new Error("Production Firebase configuration requires a project ID.");
  }
  const mismatchedField = FIREBASE_CONFIG_FIELDS.find(
    (field) =>
      String(firebaseConfig[field] || "") !==
      String(firebaseClientDefaults[field] || ""),
  );
  if (mismatchedField) {
    throw new Error(
      `Production Firebase ${mismatchedField} does not match the configured Urban Unity Network project.`,
    );
  }
  return true;
}

export function getCommunityInformationRuntimeConfig(
  env = process.env,
  firebaseConfig = {},
) {
  const production = isProduction(env);
  assertProductionFirebaseConfig(env, firebaseConfig);

  if (production) {
    const enabledForProduction = enabled(
      env.REACT_APP_UUN_COMMUNITY_AGENT_PRODUCTION_ENABLED,
    );
    return {
      enabled: enabledForProduction,
      runtime: "production",
      providerMode: enabledForProduction ? "openai" : null,
      providerLabel: enabledForProduction
        ? "OpenAI provider — production"
        : "Community Information Agent disabled",
    };
  }

  const enabledLocally = isCommunityAgentLocalMode(env);
  if (enabled(env.REACT_APP_UUN_COMMUNITY_AGENT_ENABLED)) {
    // Reuse the strict local checks so an enabled development feature cannot
    // drift onto a live project or connect only some of its emulators.
    getLocalFirebaseEmulatorConfig(env, firebaseConfig);
  }
  return {
    enabled: enabledLocally,
    runtime: "local",
    providerMode: "mock",
    providerLabel: "Mock provider — synthetic data only",
  };
}
